import {useCollection} from "../hooks/useFirestore";
import {useAuth} from "@clerk/clerk-react";
import { Collection } from "../types/collection";
import {
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    List,
    ListItem,
    ListItemText,
    ListItemButton,
    ListItemAvatar,
    Avatar,
    Box,
    CircularProgress,
    Paper
} from "@mui/material";
import {Add as AddIcon, Folder as FolderIcon, Search as SearchIcon} from '@mui/icons-material';
import {addDoc, collection} from 'firebase/firestore';
import {db} from '../firebase';
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import InputAdornment from '@mui/material/InputAdornment';

export default function CollectionLayout() {
    const { userId } = useAuth()
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [newCollectionName, setNewCollectionName] = useState('');
    const [creating, setCreating] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setNewCollectionName('');
    };

    const createCollection = async () => {
        if (newCollectionName.trim()) {
            try {
                setCreating(true);
                await addDoc(collection(db, `users/${userId}/collections`), {
                    name: newCollectionName.trim(),
                    createdAt: new Date(),
                    updatedAt: new Date(),
                    itemCount: 0,
                });
                handleClose();
            } catch (error) {
                console.error("Error creating collection: ", error);
            } finally {
                setCreating(false);
            }
        }
    };

    const {snapshot, error, isLoading} = useCollection<Collection>(`users/${userId}/collections`);

    const filteredCollections = useMemo(() => {
        if (!searchQuery.trim()) return snapshot;
        const query = searchQuery.toLowerCase();
        return snapshot.filter(c => c.name.toLowerCase().includes(query));
    }, [snapshot, searchQuery]);

    return (
        <div>
            <Box 
                sx={{
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between', 
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: { xs: 1.5, sm: 2 },
                    marginBottom: { xs: '12px', sm: '20px' },
                    px: { xs: 0.5, sm: 0 }
                }}
            >
                <Typography 
                    variant="h4" 
                    sx={{
                        fontSize: { xs: '1.75rem', sm: '2.125rem' },
                        fontWeight: 500
                    }}
                >
                    Collections
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleClickOpen}
                    startIcon={<AddIcon/>}
                    sx={{ 
                        width: { xs: '100%', sm: 'auto' } 
                    }}
                >
                    Create Collection
                </Button>
                <Dialog
                    open={open}
                    onClose={handleClose}
                    fullWidth
                    maxWidth="sm"
                    sx={{
                        '& .MuiDialog-paper': {
                            margin: { xs: '16px', sm: '32px' },
                            width: { xs: 'calc(100% - 32px)', sm: '500px' },
                            maxWidth: { xs: 'calc(100% - 32px)', sm: '500px' },
                        }
                    }}
                >
                    <DialogTitle>Create New Collection</DialogTitle>
                    <DialogContent>
                        <TextField
                            autoFocus
                            margin="dense"
                            label="Collection Name"
                            type="text"
                            fullWidth
                            value={newCollectionName}
                            onChange={(e) => setNewCollectionName(e.target.value)}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleClose} disabled={creating}>Cancel</Button>
                        <Button 
                            onClick={createCollection} 
                            color="primary"
                            disabled={!newCollectionName.trim() || creating}
                        >
                            {creating ? <CircularProgress size={24} /> : 'Create'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
            {!isLoading && !error && snapshot.length > 0 && (
                <TextField
                    placeholder="Search collections..."
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: 'text.secondary' }} />
                                </InputAdornment>
                            ),
                        },
                    }}
                    sx={{ mb: 2 }}
                />
            )}
            {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Paper 
                    elevation={0} 
                    sx={{ 
                        p: 3, 
                        bgcolor: 'error.light', 
                        color: 'error.dark',
                        borderRadius: 2
                    }}
                >
                    <Typography>Error: {error.message}</Typography>
                </Paper>
            ) : snapshot.length === 0 ? (
                <Paper 
                    elevation={0} 
                    sx={{ 
                        p: 4, 
                        textAlign: 'center', 
                        borderRadius: 2,
                        bgcolor: 'action.hover' 
                    }}
                >
                    <Typography variant="body1">
                        No collections found. Create your first collection to get started!
                    </Typography>
                </Paper>
            ) : filteredCollections.length === 0 && searchQuery.trim() ? (
                <Paper
                    elevation={0}
                    sx={{
                        p: 4,
                        textAlign: 'center',
                        borderRadius: 2,
                        bgcolor: 'action.hover'
                    }}
                >
                    <Typography variant="body1">
                        No collections matching "{searchQuery.trim()}"
                    </Typography>
                </Paper>
            ) : (
                <List
                    sx={{
                        width: '100%',
                        bgcolor: 'background.paper',
                        borderRadius: { xs: '4px', sm: '8px' },
                        overflow: 'hidden',
                        mx: { xs: -0.5, sm: 0 }
                    }}
                >
                    {filteredCollections.map(collection => (
                        <ListItem 
                            key={collection.id} 
                            disablePadding
                            sx={{ 
                                borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                                '&:last-child': {
                                    borderBottom: 'none'
                                }
                            }}
                        >
                            <ListItemButton 
                                onClick={() => navigate(`/h/collection/${collection.id}`)}
                                sx={{ 
                                    py: { xs: 2, sm: 1.5 }
                                }}
                            >
                                <ListItemAvatar>
                                    <Avatar 
                                        sx={{ 
                                            bgcolor: 'primary.light',
                                            color: 'primary.dark'
                                        }}
                                    >
                                        <FolderIcon />
                                    </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                    primary={
                                        <Typography 
                                            variant="body1" 
                                            sx={{ 
                                                fontWeight: 500,
                                                fontSize: { xs: '1rem', sm: '1.1rem' }
                                            }}
                                        >
                                            {collection.name}
                                        </Typography>
                                    }
                                    secondary={
                                        <Typography 
                                            variant="body2" 
                                            sx={{ 
                                                color: 'text.secondary',
                                                fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                            }}
                                        >
                                            {`${collection.itemCount || 0} items • Last updated: ${collection.updatedAt?.toDate().toLocaleDateString()}`}
                                        </Typography>
                                    }
                                />
                            </ListItemButton>
                        </ListItem>
                    ))}
                </List>
            )}
        </div>
    )
}
